/**
 * TaintSkillEffect — Poison tree foundation active: contact poison primer.
 *
 * Arms the player's next contact hit to deliver a `poison` DoT to the
 * target. The prime window (8 seconds) lapses silently if no hit lands
 * before it expires. Cooldown starts immediately on activation.
 *
 * Supports upgrade keys via resolver:
 * - Potent Compound: `dotDamagePerTick` (additive) — +1 per tick intensity
 * - Lingering Formula: `dotDurationMs` (additive) — +2000ms duration
 * - Toxicologist (tree passive): `dotDamageMultiplier` (product) — ×1.25
 *   intensity across all poison applications
 *
 * The Acid Burn combo (×1.25 when both `burning` and `poison` are active)
 * is enforced by CombatHitResolver, not this skill.
 *
 * Virulent Touch replaces this skill on unlock. Pre-evolution upgrades
 * (Potent Compound, Lingering Formula) transfer via resolver queries on
 * `enhancesId: 'taint'`.
 *
 * Implements SkillEffect directly for full cooldown and timer control.
 * Pure logic — no Phaser deps.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-066 (source-tagged DoT coexistence)
 * @see poison-spec.md § Taint
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface TaintDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getTreePassiveEffect(
      treeName: string,
      key: string,
      aggregation: 'sum' | 'product' | 'max' | 'min',
    ): number;
  };
  readonly statusEffects: {
    apply(defId: string, opts: {
      sourceSkillId: string;
      durationMs: number;
      intensity: number;
    }): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface TaintConfig {
  readonly cooldownMs: number;       // fast tier = 400ms
  readonly primeWindowMs: number;    // window before prime lapses silently
  readonly baseIntensity: number;    // base DoT damage per tick
  readonly baseDurationMs: number;   // base DoT duration
}

const DEFAULT_CONFIG: TaintConfig = {
  cooldownMs: 400,
  primeWindowMs: 8000,
  baseIntensity: 3,
  baseDurationMs: 4000,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class TaintSkillEffect implements SkillEffect {
  readonly skillId = 'taint';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: TaintDeps;
  private readonly config: TaintConfig;

  private _cooldownRemaining = 0;
  private _taintPrimed = false;
  private _primeWindowRemaining = 0;
  private _activationsCount = 0;
  private _hitsPoisoned = 0;
  private _primeTimeouts = 0;

  constructor(deps: TaintDeps, config: Partial<TaintConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get isPrimed(): boolean { return this._taintPrimed; }
  get primeWindowRemaining(): number { return this._primeWindowRemaining; }
  get activationsCount(): number { return this._activationsCount; }
  get hitsPoisoned(): number { return this._hitsPoisoned; }
  get primeTimeouts(): number { return this._primeTimeouts; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    this._taintPrimed = true;
    this._primeWindowRemaining = this.config.primeWindowMs;
    this._cooldownRemaining = this.config.cooldownMs;
    this._activationsCount++;

    return true;
  }

  deactivate(): void {
    // Instant — no toggle
  }

  isActive(): boolean {
    return false;
  }

  /**
   * Called by CombatHitResolver when any contact hit (melee or Blades) lands.
   * If primed, applies poison DoT to the target and clears the prime state.
   * Returns true if poison was applied, false if not primed.
   */
  notifyHitLanded(): boolean {
    if (!this._taintPrimed) return false;

    const intensity = this.resolveIntensity();
    const durationMs = this.resolveDurationMs();

    this.deps.statusEffects.apply('poison', {
      sourceSkillId: 'taint',
      durationMs,
      intensity,
    });

    this._taintPrimed = false;
    this._primeWindowRemaining = 0;
    this._hitsPoisoned++;

    return true;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }

    if (this._taintPrimed) {
      this._primeWindowRemaining -= delta;
      if (this._primeWindowRemaining <= 0) {
        // Prime lapses silently — no cooldown re-trigger, no feedback
        this._taintPrimed = false;
        this._primeWindowRemaining = 0;
        this._primeTimeouts++;
      }
    }
  }

  destroy(): void {
    this._taintPrimed = false;
    this._primeWindowRemaining = 0;
    this._cooldownRemaining = 0;
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private resolveIntensity(): number {
    const base = this.config.baseIntensity;
    const bonus = this.deps.resolver.getAdditiveEffect('taint', 'dotDamagePerTick');
    const multiplier = this.deps.resolver.getTreePassiveEffect('poison', 'dotDamageMultiplier', 'product');
    return (base + bonus) * multiplier;
  }

  private resolveDurationMs(): number {
    const base = this.config.baseDurationMs;
    const bonus = this.deps.resolver.getAdditiveEffect('taint', 'dotDurationMs');
    return base + bonus;
  }
}
